import React, { createContext, useContext, useEffect, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert, { AlertColor } from "@mui/material/Alert";
import { SnackbarCloseReason } from "@mui/material/Snackbar";
import { NeutralColor, SemanticColor } from "node-library/dist/colors";
import { Box, LinearProgress } from "@mui/material";

interface SnackbarProps {
	duration: number;
	severity: AlertColor | undefined;
	message: string;
	description?: string;
	icon?: React.ReactNode; // Optional icon prop
	actionButtons?: Array<React.ReactElement>;
	presistent?: boolean;
}

interface SnackbarContextType {
	openSnackbar: (
		message: string,
		severity?: AlertColor,
		description?: string,
		icon?: React.ReactNode,
		actionButtons?: Array<React.ReactElement>,
		presistent?: boolean
	) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
	undefined
);

const colorMapping: Record<
	AlertColor,
	{ background: string; border: string; headerColor: string }
> = {
	success: {
		background: SemanticColor.SUCCESS_100, // Light green
		border: SemanticColor.SUCCESS,
		headerColor: SemanticColor.SUCCESS_300,
	},
	error: {
		background: SemanticColor.ERROR_100, // Light red
		border: SemanticColor.ERROR,
		headerColor: SemanticColor.ERROR_300,
	},
	warning: {
		background: SemanticColor.WARNING_100, // Light orange
		border: SemanticColor.WARNING,
		headerColor: SemanticColor.WARNING_300,
	},
	info: {
		background: "rgba(33, 150, 243, 0.7)", // Light blue
		border: "blue",
		headerColor: SemanticColor.SUCCESS_300,
	},
};

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [snackbarProps, setSnackbarProps] = useState<SnackbarProps>({
		duration: 6000,
		severity: undefined,
		message: "",
		description: "",
		icon: null,
	});
	const [open, setOpen] = useState(false);
	const [progress, setProgress] = useState(0);
	const [isHovered, setIsHovered] = useState(false); // New state to track hover

	const { background, border, headerColor } =
		colorMapping[snackbarProps.severity || "success"];

	const openSnackbar = (
		message: string,
		severity?: AlertColor,
		description?: string,
		icon?: React.ReactNode,
		actionButtons?: Array<React.ReactElement>,
		presistent?: boolean
	) => {
		setSnackbarProps({
			duration: 6000,
			severity,
			message,
			description,
			icon,
			actionButtons,
			presistent,
		});
		setOpen(true);
		setProgress(0); // Reset progress
	};

	const handleClose = (
		event?: React.SyntheticEvent | Event,
		reason?: SnackbarCloseReason
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
		setTimeout(() => {
			setProgress(0);
		}, 1);
	};

	useEffect(() => {
		if (open && !snackbarProps.presistent) {
			let timer: NodeJS.Timeout;

			const totalDuration = snackbarProps.duration; // Total duration in ms

			const updateProgress = () => {
				if (!isHovered) {
					setProgress((prev) => {
						if (prev >= 100) {
							clearInterval(timer);
							handleClose(); // Close when finished
							return 100; // Ensure it's set to 100
						}
						return prev + (100 / totalDuration) * 100; // Increment progress
					});
				}
			};

			timer = setInterval(updateProgress, 100);

			return () => clearInterval(timer);
		}
	}, [open, isHovered, snackbarProps.duration, snackbarProps.presistent]);

	return (
		<SnackbarContext.Provider value={{ openSnackbar }}>
			{children}
			<Snackbar
				open={open}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			>
				<Box
					sx={{ width: "360px", position: "relative" }}
					onMouseEnter={() => setIsHovered(true)} // Set hover state to true
					onMouseLeave={() => setIsHovered(false)} // Set hover state to false
				>
					<Alert
						onClose={handleClose}
						severity={snackbarProps.severity}
						variant="outlined"
						sx={{
							backgroundColor: background, // Dynamic background color
							border: `1px solid ${border}`, // Dynamic border color
							position: "relative",
						}}
						icon={false} // Disable the default icon
					>
						<div
							className="snackbar-content typography-body-02 wrap-text"
							style={{ color: headerColor }}
						>
							{snackbarProps.icon}
							<div className="typography-bold"> {snackbarProps.message} </div>
							{snackbarProps.description && (
								<div
									className="snackbar-description"
									style={{ color: NeutralColor.NEUTRAL_GREY_700 }}
								>
									{snackbarProps.description}
								</div>
							)}
						</div>
						{snackbarProps.actionButtons && (
							<div
								style={{
									display: "flex",
									gap: "5px",
									justifyContent: "flex-end",
								}}
							>
								{snackbarProps.actionButtons.map((node: React.ReactElement) => {
									return React.cloneElement(node, {
										onClick: () => {
											handleClose(); // Close the Snackbar
											if (node.props.onClick) {
												node.props.onClick(); // Call the action's onClick if provided
											}
										},
									});
								})}
							</div>
						)}
						{!snackbarProps.presistent && (
							<LinearProgress
								variant="determinate"
								value={progress}
								sx={{
									position: "absolute",
									bottom: 0,
									left: 16,
									right: 0,
									width: "calc(100% - 32px)",
									backgroundColor: background,

									"& .MuiLinearProgress-bar": {
										backgroundColor: headerColor,
									},
								}}
							/>
						)}
					</Alert>
				</Box>
			</Snackbar>
		</SnackbarContext.Provider>
	);
};

export const useSnackbar = () => {
	const context = useContext(SnackbarContext);
	if (!context) {
		throw new Error("useSnackbar must be used within a SnackbarProvider");
	}
	return context;
};
